import {
  AddCircle,
  ArrowCircleLeftOutlined,
  CloseOutlined,
  FilePresentOutlined,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
} from "@mui/material";
import { getToken, getUserId } from "auth/authenticate";
import axios from "axios";
import ErrMsg from "components/notification/ErrMsg";
import { APPOINTMENT_PURPOSES, PAYMENT_CATEGORY } from "constants/constants";
import { useFormik } from "formik";
import useDeedToast from "hook/useDeedToast";
import {
  Customer,
  CurrencyType,
  PayhereCheckout,
  CheckoutParams,
  Payhere,
  AccountCategory,
} from "payhere-js-sdk";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const PayHereComponent = ({
  patientData,
  doctorData,
  appointmentId,
  appointmentData,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectFile, setSelectFile] = useState([]);
  const [selectedFileNames, setSelectedFileNames] = useState([]);
  const { deedToast } = useDeedToast();

  if (process.env.REACT_APP_PAYHERE_ENV === "SANDBOX") {
    Payhere.init(
      process.env.REACT_APP_PAYHERE_SANDBOX_MERCHANT_ID,
      AccountCategory.SANDBOX
    );
  } else {
    Payhere.init(
      process.env.REACT_APP_PAYHERE_MERCHANT_ID,
      AccountCategory.LIVE
    );
  }

  // alert("UserId:" + getUserId());

  function onPayhereCheckoutError(errorMsg) {
    alert(errorMsg);
  }

  const formik = useFormik({
    initialValues: {
      purpose: appointmentData.purpose ? appointmentData?.purpose : "",
      medicalDetail: "",
      type: appointmentData.appointmentType
        ? appointmentData?.appointmentType
        : "",
      message: "",
      patient: appointmentData.patientId,
      attachments: [],
    },
    validate: (values) => {
      const errors = {};

      // Add validation rules for each required field
      if (!values.purpose) {
        errors.purpose = "Purpose is required";
      }

      if (!values.type) {
        errors.type = "Type is required";
      }

      return errors;
    },
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  const validateFileType = (file) => {
    const allowedTypes = [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/webp",
      "application/pdf",
      "text/plain",
      "application/plain",
      "text/csv",
    ];
    return allowedTypes.includes(file.type);
  };

  const validateFileSize = (file) => {
    const maxSizeBytes = 10 * 1024 * 1024; // 10MB limit
    return file.size <= maxSizeBytes;
  };

  const handleSelectedFile = (event) => {
    const file = event.target.files[0];

    // Validate file type
    if (validateFileType(file)) {
      if (validateFileSize(file)) {
        let selectFiles = [...selectFile];
        let selectFileNames = [...selectedFileNames];
        selectFiles.push(file);
        selectFileNames.push(file.name);
        setSelectFile(selectFiles);
        setSelectedFileNames(selectFileNames);

        // Update Formik form state
        formik.setFieldValue("attachments", selectFiles);

        // Clear previous file error
        formik.setFieldError("attachments", "");
      } else {
        deedToast("file size should be less than 10mb", "danger");
      }
    } else {
      // Set file type error
      deedToast(
        "Invalid file type. Allowed types are: PNG, JPEG, JPG, WebP, PDF, Text",
        "danger"
      );
    }
  };

  const handleRemoveFile = (ind) => {
    const list = [...selectFile];
    const nameList = [...selectedFileNames];
    list.splice(ind, 1);
    nameList.splice(ind, 1);
    setSelectFile(list);
    setSelectedFileNames(nameList);
  };

  async function createPayment(id, doctorData) {
    let config = {
      headers: {
        Authorization: "Bearer " + getToken(),
      },
    };
    const payResponse = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}payment`,
      {
        orderRef: id,
        txnAmount: doctorData.displayingFee,
        category: PAYMENT_CATEGORY.APPOINTMENT,
        userId: getUserId(),
        payType: "FP",
      },
      config
    );

    if (payResponse.status == 201) {
      return payResponse;
    } else {
      alert("Payment not created");
      return 0;
    }
  }

  async function checkout(values) {
    const customer = new Customer({
      first_name: patientData?.name,
      last_name: patientData?.name,
      phone: patientData?.User?.phoneNumber,
      email: patientData?.User?.email,
      address: patientData?.address,
      city: patientData?.district,
      country: "Sri Lanka",
    });

    // console.log("customer :" + JSON.stringify(customer));
    // alert("customer :" + JSON.stringify(customer));

    const paymentResponse = await createPayment(appointmentId, doctorData);
    // alert("appointment Id : " + appointmentId);
    // @ts-ignore
    const checkoutData = new CheckoutParams({
      returnUrl: `${process.env.REACT_APP_FRONTEND_BASE_URL}doctor/:id/add-appointments/fill-details/make-payment/thank-you/${appointmentId}`,
      cancelUrl: `${process.env.REACT_APP_FRONTEND_BASE_URL}payment/cancel`,
      notifyUrl: `${process.env.REACT_APP_BACKEND_URL}payment/confirmation`,
      order_id: paymentResponse?.data.data.paymentRef,
      itemTitle: PAYMENT_CATEGORY.APPOINTMENT,
      currency: CurrencyType.LKR,
      amount: Math.floor(doctorData?.displayingFee),
      hash: paymentResponse?.data.data.hash,
    });
    const checkout = new PayhereCheckout(
      customer,
      checkoutData,
      onPayhereCheckoutError
    );
    setTimeout(() => {
      checkout.start();
    }, 100);
  }

  const handleSubmit = (values) => {
    setLoading(true);
    try {
      var bodyFormData = new FormData();

      selectFile.forEach((file) => {
        bodyFormData.append("files", file);
      });
      bodyFormData.append("doctorId", appointmentData.doctorId);
      bodyFormData.append("patientId", appointmentData.patientId);
      bodyFormData.append("date", appointmentData.date);
      bodyFormData.append("slotTime", appointmentData.slotTime);
      // bodyFormData.append("slotType", state?.slotType);
      bodyFormData.append("appointmentType", appointmentData.appointmentType);
      bodyFormData.append("purpose", appointmentData.purpose);
      bodyFormData.append(
        "medicalDetails",
        values.medicalDetail.length !== 0 ? values.medicalDetail : "  "
      );
      bodyFormData.append("message", values.message);
      if (doctorData) {
        bodyFormData.append("amount", doctorData.displayingFee);
      }

      bodyFormData.append("paymentId", "test_id");
      bodyFormData.append("parentId", appointmentData.patientId);

      axios({
        method: "post",
        url: `${process.env.REACT_APP_BACKEND_URL}appointment/patient`,
        data: bodyFormData,
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: "Bearer " + getToken(),
        },
      })
        .then(function (response) {
          if (response) {
            setLoading(false);
            deedToast(response.data?.message);
            // navigate("make-payment");
            /*enable redirect payment page =================*/
            checkout(response.data.data);
            // setAppointment(response.data.data);
          }
        })
        .catch(function (response) {
          //handle error
          setLoading(false);
          // console.log(response);
        });
    } catch (e) {
      console.log(e);
    }
  };

  //   const updateAppointment = () => {
  //     try {
  //       var bodyFormData = new FormData();

  //       bodyFormData.append("status", APPOINTMENT_STATUS.COMPLETED);

  //       // console.log(bodyFormData);
  //       axios({
  //         method: "put",
  //         url: `${process.env.REACT_APP_BACKEND_URL}appointment/patient/${selectedAppointment.appointmentId}`,
  //         data: bodyFormData,
  //         headers: {
  //           "Content-Type": "multipart/form-data",
  //           Authorization: "Bearer " + getToken(),
  //         },
  //       })
  //         .then(function (response) {
  //           if (response) {
  //             // console.log(response);
  //           }
  //         })
  //         .catch(function (response) {
  //           //handle error
  //           // console.log(response);
  //         })
  //         .finally(() => {});
  //     } catch (e) {
  //       console.log(e);
  //     }
  //   };

  if (loading) {
    return (
      <center style={{ marginTop: 200 }}>
        <CircularProgress />
      </center>
    );
  }

  return (
    <>
      <div className="addAppointmentMain">
        <div className="pharmacyInfoMainHeading">
          <div onClick={() => navigate(-1)}>
            <ArrowCircleLeftOutlined className="headingIcon" fontSize="large" />
          </div>
          <h3>Add Appointment</h3>
        </div>
        <div>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              "& > :not(style)": {
                ml: 2,
                mr: 2,
                pl: 4,
                pb: 2,
                pr: 4,
                width: "100%",
                height: "auto",
                background:
                  "linear-gradient(90.22deg, #0536E3 -19.45%, #00E9ED 142.36%)",
                color: "#ffffff",
              },
            }}
          >
            <Paper elevation={0}>
              <Grid container>
                <Grid item lg={7} md={6} xs={12}>
                  <Stack direction="row" spacing={2}>
                    <div className="addAppointmentProfileAvatarName">
                      <h2>{doctorData && doctorData?.name} </h2>
                      <p>
                        {doctorData?.serviceCategories?.map((item, index) => {
                          if (
                            index ===
                            doctorData?.serviceCategories?.length - 1
                          ) {
                            return <span key={index}> {item} </span>;
                          } else {
                            return <span key={index}> {item} |</span>;
                          }
                        })}
                      </p>
                    </div>
                  </Stack>
                </Grid>
                <Grid
                  item
                  lg={5}
                  md={6}
                  xs={12}
                  mt={-3}
                  className="appointmentPriceGrid"
                >
                  <p>
                    LKR {doctorData && (doctorData.displayingFee ?? "700")}
                    {".00"}
                  </p>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </div>

        <div className="docterProfileScroll">
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              "& > :not(style)": {
                ml: 2,
                mr: 2,
                pl: 4,
                pb: 2,
                pr: 4,
                width: "100%",
                height: "auto",
              },
            }}
          >
            {/* info1 info2 edit icon css from doctor profile component */}
            <Paper elevation={0}>
              <h2>Fill This Details</h2>
              {/* <div className="subheading">Fill in required fields</div> */}
              {/* <form  onSubmit={formik.handleSubmit}> */}
              <form>
                <Grid container>
                  <Grid item lg={3} md={5} xs={12}>
                    <div className="info1">
                      <div>
                        <h6>Patient Name</h6>

                        <p>{patientData?.name}</p>
                      </div>
                    </div>
                    <div className="info1 info2">
                      <div>
                        <h6>Medical Details</h6>
                        <TextField
                          variant="standard"
                          size="small"
                          placeholder="Type"
                          className="messageTextField"
                          id="medicalDetail"
                          onChange={formik.handleChange}
                          value={formik.values.medicalDetail}
                          InputProps={{
                            disableUnderline: true,
                          }}
                        />
                      </div>
                    </div>
                  </Grid>
                  <Grid item lg={3} md={5} xs={12} mt={2}>
                    <div className="info1 selectInfo">
                      <div>
                        <h6>Purpose*</h6>
                        <div className="select-data">
                          <select
                            name="purpose"
                            id="purpose"
                            onChange={formik.handleChange}
                            value={formik.values.purpose}
                            required
                          >
                            <option>Select (ex. : Consultation)</option>
                            <option value={APPOINTMENT_PURPOSES.REPORT_READING}>
                              Report Reading
                            </option>
                            <option value={APPOINTMENT_PURPOSES.CONSULTING}>
                              Consultation
                            </option>
                            {/* <option value="Consultation">Consultation</option> */}
                          </select>
                        </div>
                        <div className="error-message">
                          {formik.errors.purpose && formik.touched.purpose && (
                            <ErrMsg msg={formik.errors.purpose} />
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="info1 info2 selectInfo">
                      <div>
                        <h6>Type*</h6>
                        <div className="select-data">
                          <select
                            name="type"
                            id="type"
                            onChange={formik.handleChange}
                            value={formik.values.type}
                            required
                          >
                            <option>Select (ex. : Online Video Chat)</option>
                            <option value="ONLINE">Online Video Chat</option>
                            {/* <option value="Voice Chat">Voice Chat</option> */}
                            <option value="PHYSICAL">Physical</option>
                          </select>
                        </div>
                        <div className="error-message">
                          {formik.errors.type && formik.touched.type && (
                            <ErrMsg msg={formik.errors.type} />
                          )}
                        </div>
                      </div>
                    </div>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item lg={6} md={10} xs={12}>
                    <div className="info2 desc">
                      <div>
                        <h6>Message</h6>
                        <TextField
                          variant="standard"
                          size="small"
                          multiline
                          rows={3}
                          placeholder="Type"
                          id="message"
                          className="messageTextField"
                          onChange={formik.handleChange}
                          value={formik.values.message}
                          InputProps={{
                            disableUnderline: true,
                          }}
                        />
                      </div>
                    </div>
                  </Grid>
                </Grid>
                <Grid container mt={1}>
                  <Grid item lg={3} md={5} xs={12}>
                    <div className="info1 attachment">
                      <div>
                        <h6>Attachments</h6>
                        {selectedFileNames.map((item, index) => {
                          return (
                            <div className="file-name" key={index}>
                              <FilePresentOutlined fontSize="small" />
                              <p>{item}</p>
                              <div
                                className="delete-file"
                                onClick={() => {
                                  handleRemoveFile(index);
                                }}
                              >
                                <CloseOutlined />
                              </div>
                            </div>
                          );
                        })}

                        <div className="addmore-file">
                          <label>
                            Add New Attachment
                            <div className="add-icon">
                              <AddCircle fontSize="small" />
                            </div>
                          </label>
                          <input
                            type="file"
                            name="attachments"
                            id="attachments"
                            onChange={handleSelectedFile}
                          />
                        </div>
                      </div>
                      {formik.errors.attachments &&
                        formik.touched.attachments && (
                          <ErrMsg msg={formik.errors.attachments} />
                        )}
                    </div>
                  </Grid>
                </Grid>

                {/* <Button
                  variant="contained"
                  type="submit"
                  className="nextStepBtn"
                  // onClick={handleAddFile}
                    disabled={loading}
                >
                  Next Step
                </Button> */}
              </form>

              <Button
                type="button"
                variant="contained"
                className="nextStepBtn"
                disabled={loading}
                onClick={formik.handleSubmit}
              >
                Next Step
              </Button>
            </Paper>
          </Box>
        </div>
      </div>
      <hr />

      <div>
        <p>{JSON.stringify(doctorData)}</p>
      </div>
    </>
  );
};

export default PayHereComponent;
